import type { ExpressionGraph, ExpressionNode } from '../../state/types';
import { functionRegistry } from '../../utils/functionRegistry';

type SetExpressionGraph = (updater: (prev: ExpressionGraph) => ExpressionGraph) => void;

/** Clears one operand slot; for variadic (unlimited) non-series-aware functions, trims trailing null slots when clearing the last index above min arity. */
export function operandIdsAfterClearingSlot(
  operandIds: (string | null)[],
  slotIndex: number,
  parentFunctionId: string
): (string | null)[] {
  const newOperandIds = operandIds.map((id, i) => (i === slotIndex ? null : id));
  const countBefore = operandIds.length;
  const clearedLastSlot = slotIndex === countBefore - 1;
  const def = functionRegistry.get(parentFunctionId);
  if (!def || def.maxParameters != null) {
    return newOperandIds;
  }
  const isSeriesAwareOnly =
    def.parameters.length > 0 &&
    def.parameters.every(
      (p: { type: string }) => p.type === 'seriesAware' || p.type === 'seriesAwareWindow'
    );
  if (!isSeriesAwareOnly && clearedLastSlot && countBefore > def.minParameters) {
    const out = [...newOperandIds];
    while (out.length > def.minParameters && out[out.length - 1] == null) {
      out.pop();
    }
    return out;
  }
  return newOperandIds;
}

export function createInsertLiteralHandler(setExpressionGraph: SetExpressionGraph) {
  return (parentId: string, index: number, value: number | string, isString?: boolean) => {
    const literalId = `literal-${Math.random().toString(36).slice(2)}`;
    const literalValue: number | string = isString
      ? (typeof value === 'string' ? value : String(value))
      : (typeof value === 'number' ? value : Number(value));
    const newLiteralNode: ExpressionNode = {
      id: literalId,
      type: 'literal',
      value: literalValue,
    };

    setExpressionGraph((prev) => {
      const parent = prev.expressions[parentId];
      if (!parent || parent.type !== 'function') return prev;

      const oldOperandIds = parent.operandIds || [];
      const updatedOperandIds = [...oldOperandIds];
      updatedOperandIds[index] = literalId;

      const updatedParent: ExpressionNode = {
        ...parent,
        operandIds: updatedOperandIds,
      };

      return {
        ...prev,
        expressions: {
          ...prev.expressions,
          [literalId]: newLiteralNode,
          [parentId]: updatedParent,
        },
      };
    });
  };
}

export function createUpdateEpsilonHandler(setExpressionGraph: SetExpressionGraph) {
  return (expressionId: string, epsilon: number) => {
    setExpressionGraph((prev) => {
      const node = prev.expressions[expressionId];
      if (node?.type !== 'function') return prev;
      return {
        ...prev,
        expressions: {
          ...prev.expressions,
          [expressionId]: { ...node, epsilon },
        },
      };
    });
  };
}

export function createRemoveOperandHandler(setExpressionGraph: SetExpressionGraph) {
  return (parentId: string, index: number) => {
    setExpressionGraph((prev) => {
      const parent = prev.expressions[parentId];
      if (!parent || parent.type !== 'function') return prev;

      const operandIdToRemove = parent.operandIds[index];
      if (!operandIdToRemove) return prev;

      const idsToDelete = new Set<string>();
      const queue = [operandIdToRemove];

      while (queue.length > 0) {
        const id = queue.shift()!;
        if (idsToDelete.has(id)) continue;
        idsToDelete.add(id);

        const node = prev.expressions[id];
        if (node?.type === 'function') {
          for (const childId of node.operandIds || []) {
            if (childId) queue.push(childId);
          }
        }
      }

      const newOperandIds = operandIdsAfterClearingSlot(parent.operandIds, index, parent.functionId);
      const newExpressions = { ...prev.expressions };
      for (const id of idsToDelete) {
        delete newExpressions[id];
      }
      newExpressions[parentId] = {
        ...parent,
        operandIds: newOperandIds,
      };

      return {
        ...prev,
        expressions: newExpressions,
      };
    });
  };
}

export function createClearExpressionHandler(setExpressionGraph: SetExpressionGraph) {
  return () => {
    setExpressionGraph(() => ({ rootId: null, expressions: {} }));
  };
}
